<script setup lang="ts">
import { computed } from 'vue'

import { useCommunicationTranslations } from '../composables/useCommunicationTranslations'

const props = defineProps<{
  typingUsers: Set<string> | string[]
  compact?: boolean
  translationPrefix?: string | null
}>()

const { tm } = useCommunicationTranslations(props.translationPrefix || 'communication')

const typingUsersList = computed(() => {
  return props.typingUsers instanceof Set ? Array.from(props.typingUsers) : props.typingUsers
})

const isTyping = computed(() => typingUsersList.value.length > 0)

const typingText = computed(() => {
  const users = typingUsersList.value
  if (users.length === 0) return ''
  if (users.length === 1) return tm('messages.typing_single', { user: users[0] })
  if (users.length === 2) return tm('messages.typing_double', { user1: users[0], user2: users[1] })
  return tm('messages.typing_multiple', { user: users[0], count: users.length - 1 })
})
</script>

<template>
  <Transition
    enter-active-class="transition-opacity duration-200"
    leave-active-class="transition-opacity duration-200"
    enter-from-class="opacity-0"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isTyping"
      :class="[
        'flex items-center gap-2 text-muted-foreground',
        compact ? 'text-[10px]' : 'text-sm'
      ]"
    >
      <div class="flex gap-1">
        <span
          :class="[
            'inline-block animate-bounce rounded-full bg-current [animation-delay:-0.3s]',
            compact ? 'h-1 w-1' : 'h-1.5 w-1.5'
          ]"
        />
        <span
          :class="[
            'inline-block animate-bounce rounded-full bg-current [animation-delay:-0.15s]',
            compact ? 'h-1 w-1' : 'h-1.5 w-1.5'
          ]"
        />
        <span
          :class="[
            'inline-block animate-bounce rounded-full bg-current',
            compact ? 'h-1 w-1' : 'h-1.5 w-1.5'
          ]"
        />
      </div>
      <slot>
        <span>{{ typingText }}</span>
      </slot>
    </div>
  </Transition>
</template>
